import React from 'react';
import { useEffect, useRef } from 'react';
import { useImmer } from 'use-immer';
import { motion, AnimatePresence } from 'framer-motion/dist/framer-motion';
import FieldMessage from './FieldMessage/FieldMessage';
import FormMessage from './FormMessage/FormMessage';
import FormSubmitButton from './FormSubmitButton/FormSubmitButton';

const MvpMailSubscription = ({ jsonUrl, LoaderIcon, ErrorIcon, SuccessIcon }) => {
  const inputRefs = useRef({});
  const [data, setData] = useImmer({});
  const [loading, setLoading] = useImmer(true);
  const [status, setStatus] = useImmer('IDLE'); // IDLE || PENDING || SUCCESS || INVALID || ERROR
  const variants = {
    initial: { opacity: 0, y: '-50%' },
    animate: { opacity: 1, y: '0' },
  };

  useEffect(() => {
    let fetchCancelled = false;

    fetch(jsonUrl)
      .then((res) => (res.ok ? res.json() : Promise.reject(`${res.status}: ${res.statusText}`)))
      .then((res) => {
        if (!fetchCancelled) {
          setData(res);
          setLoading(false);
          updateFields();
        }
      })
      .catch((error) => {
        setStatus('ERROR');
        setLoading(false);
        console.error(error);
      });

    return () => {
      fetchCancelled = true;
    };
  }, []);

  const onChangeHandler = (e) => {
    const field = data.fields.find((field) => field.id === e.target.id);

    setData(({ fields }) => {
      fields.find((field) => field.id === e.target.id).value = e.target.value;
    });
  };

  const onSubmitHandler = (e) => {
    e.preventDefault();

    if (status === 'PENDING') return;

    setStatus('PENDING');

    const postUrl = esf.adminUrl.concat('?action=', esf.action);
    const formData = new FormData();

    data.fields.forEach((field) => formData.append(field.name, field.value));
    formData.append('nonce', esf.nonce);

    fetch(postUrl, {
			method: 'POST',
			body: formData,
		})
			.then((res) => (res.ok ? res.json() : Promise.reject(`${res.status}: ${res.statusText}`)))
			.then((res) => {
				if (res.success) {
					return setData((data) => {
						data.message.success = res.data.message;
					});
				}
				setData((data) => {
					data.message.error = res.data.message;
				});

				res.success ? setStatus('SUCCESS') : setStatus('ERROR');
			})
			.catch((error) => {
				setStatus('ERROR');
				setData((data) => {
					data.message.error = `${data.message.error} (${error})`;
				});
			});
  };

  const onInvalidHandler = (e) => {
    e.preventDefault();
    setStatus('INVALID');
    updateFields();
  };

  const onClickHandler = () => {
    updateFields();
  };

  const updateFields = () => {
    Object.values(inputRefs.current).forEach((input) => {
      setData((data) => {
        const field = data.fields.find((field) => field.id === input.id);
        mapValidityState(field, input);
        setValidationMessage(field, input);
      });
    });
  };

  const mapValidityState = (field, input) => {
    for (let key in input.validity) {
      field.validity[key] = input.validity[key];
    }
  };

  const setValidationMessage = (field, input) => {
    if (input.validity.badInput) {
      input.setCustomValidity(field.message.badInput);
    } else if (input.validity.typeMismatch) {
      input.setCustomValidity(field.message.typeMismatch);
    } else if (input.validity.valueMissing) {
      input.setCustomValidity(field.message.valueMissing);
    } else if (input.validity.patternMismatch) {
      input.setCustomValidity(field.message.patternMismatch);
    } else {
      input.setCustomValidity('');
    }

    field.validationMessage = input.validationMessage;
  };

  if (loading === true) {
   //  return <div className="loader">{LoaderIcon ? <LoaderIcon className="loader-icon" /> : <p>Loading...</p>}</div>;
	return <div className="loader"><p>Loading...</p></div>;
  }
  return (
	<motion.form
	  initial={{ opacity: 0 }}
	  animate={{ opacity: 1 }}
	  className={`mvp-mail-subscription mvp-mail-subscription--${status.toLowerCase()}`}
	  onSubmit={onSubmitHandler}
	  onInvalid={onInvalidHandler}
	  method="POST"
	>
	<div className='mvp-mail-input-container'>
		<div className='mvp-mail-input'>
		{data.fields.map((field) => (
			<div key={field.id}>
				<input
				className={`${status === 'INVALID' && !field.validity.valid ? 'is-invalid' : ''} mvp-mail-input-input`}

				placeholder={field.placeholder}
				type={field.type}
				name={field.name}
				id={field.id}
				required={field.required}
				disabled={status === 'PENDING'}
				title={field.value === '' ? field.message?.valueMissing : field.validationMessage}
				value={field.value}
				onChange={onChangeHandler}
				pattern={field.pattern === false ? undefined : field.pattern}
				ref={(element) => (inputRefs.current[field.id] = element)}
				/>
				<label htmlFor={field.id}>{field.label}</label>
			</div>
		))}

		<FormSubmitButton status={status} data={data} onClickHandler={onClickHandler} />
		</div>
	</div>
		<AnimatePresence>
			{status === 'INVALID' && data.fields.some(field => !field.validity.valid) && (
				<motion.div initial="initial" animate="animate" exit="initial" variants={variants} transition={{ duration: 0.3, ease: 'easeInOut' }}>
				{data.fields.map((field) => (
					status === 'INVALID' && !field.validity.valid && <FieldMessage key={field.id} field={field} ErrorIcon={ErrorIcon} />
				))}
				</motion.div>
			)}
		</AnimatePresence>
		<FormMessage status={status} data={data} ErrorIcon={ErrorIcon} SuccessIcon={SuccessIcon} />


	</motion.form>
 );


};

export default MvpMailSubscription;
